import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';

export default function Auth() {
  const [mode, setMode] = useState('login');
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [totpStep, setTotpStep] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [tempToken, setTempToken] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(true);
  const [stationLogo, setStationLogo] = useState('');
  const [stationName, setStationName] = useState('Dennery FM 88.9');
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetch('/api/settings/logo').then(r => r.ok ? r.blob() : null)
      .then(b => b && setStationLogo(URL.createObjectURL(b))).catch(()=>{});
    api.get('/auth/check-setup').then(({ data }) => {
      if (data.needsSetup) setMode('setup');
      setChecking(false);
    }).catch(() => setChecking(false));
  }, []);

  const handleTotp = async () => {
    setError('');
    try {
      const { data } = await api.post('/auth/2fa/authenticate', { tempToken, code: totpCode });
      login(data.token, data.user);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid code');
    }
  };

  const handleSubmit = async () => {
    setError('');
    if (!form.email || !form.password) { setError('Email and password required'); return; }
    if (mode === 'setup' && !form.name) { setError('Name required'); return; }
    setLoading(true);
    try {
      const endpoint = mode === 'setup' ? '/auth/setup' : '/auth/login';
      const payload = mode === 'setup'
        ? { name: form.name, email: form.email, password: form.password }
        : { email: form.email, password: form.password };
      const { data } = await api.post(endpoint, payload);
      if (data.requires2fa) {
        setTempToken(data.tempToken);
        setTotpStep(true);
        setError('');
        return;
      }
      login(data.token, data.user);
      navigate('/dashboard');
    } catch (err) {
      setError(err.response?.data?.error || 'Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const inputStyle = {
    width: '100%', padding: '12px 16px', borderRadius: '10px',
    border: '1.5px solid var(--border)', background: 'var(--surface2)',
    color: 'var(--text)', fontSize: '14px', outline: 'none',
    transition: 'border-color 0.15s',
  };
  const labelStyle = {
    display: 'block', fontSize: '11px', fontWeight: '700',
    color: 'var(--text-muted)', textTransform: 'uppercase',
    letterSpacing: '0.8px', marginBottom: '7px',
  };
  const btnStyle = {
    width: '100%', padding: '14px', background: 'var(--signal)',
    color: '#fff', fontWeight: '700', fontSize: '15px',
    borderRadius: '10px', border: 'none', cursor: 'pointer',
    marginTop: '8px', letterSpacing: '0.3px',
    transition: 'opacity 0.15s',
  };
  const errorStyle = {
    background: 'var(--danger-bg)', border: '1px solid rgba(220,38,38,0.25)',
    color: 'var(--danger)', borderRadius: '8px',
    padding: '10px 14px', fontSize: '13px', marginBottom: '16px',
  };

  const LogoSection = () => (
    <div style={{ textAlign: 'center', marginBottom: '32px' }}>
      {stationLogo
        ? <img src={stationLogo} alt="logo" style={{ width: '72px', height: '72px', objectFit: 'contain', borderRadius: '16px', marginBottom: '14px', display: 'block', margin: '0 auto 14px' }} />
        : <div style={{ width: '72px', height: '72px', borderRadius: '16px', background: 'var(--signal)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '28px', fontWeight: '800', color: '#fff', margin: '0 auto 14px' }}>D</div>
      }
      <div style={{ fontSize: '18px', fontWeight: '800', color: 'var(--text)', marginBottom: '4px' }}>Dennery FM 88.9</div>
      <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>Broadcast Studio</div>
    </div>
  );

  if (checking) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--bg)' }}>
      <div style={{ color: 'var(--text-muted)', fontSize: '14px' }}>Loading…</div>
    </div>
  );

  if (totpStep) return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'var(--bg)' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px', padding: '48px 40px', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
        <LogoSection />
        <div style={{ textAlign: 'center', marginBottom: '28px' }}>
          <div style={{ fontSize: '20px', fontWeight: '800', marginBottom: '6px' }}>Two-factor auth</div>
          <div style={{ fontSize: '13px', color: 'var(--text-muted)' }}>Enter the 6-digit code from your authenticator app</div>
        </div>
        {error && <div style={errorStyle}>{error}</div>}
        <div style={{ marginBottom: '16px' }}>
          <input
            type="text" inputMode="numeric" maxLength={6}
            placeholder="000 000"
            value={totpCode}
            onChange={e => setTotpCode(e.target.value.replace(/\D/g,''))}
            onKeyDown={e => e.key === 'Enter' && handleTotp()}
            style={{ ...inputStyle, textAlign: 'center', fontSize: '28px', letterSpacing: '10px', fontWeight: '700' }}
            autoFocus
          />
        </div>
        <button style={btnStyle} onClick={handleTotp} disabled={totpCode.length !== 6}>
          Verify
        </button>
        <button
          style={{ ...btnStyle, background: 'transparent', color: 'var(--text-muted)', border: '1.5px solid var(--border)', marginTop: '8px' }}
          onClick={() => { setTotpStep(false); setTotpCode(''); setTempToken(''); }}>
          ← Back
        </button>
      </div>
    </div>
  );

  return (
    <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px', background: 'var(--bg)' }}>
      <div style={{ background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '20px', padding: '48px 40px', width: '100%', maxWidth: '400px', boxShadow: '0 4px 24px rgba(0,0,0,0.06)' }}>
        <LogoSection />

        {mode === 'setup' && (
          <div style={{ background: 'rgba(232,92,53,0.08)', border: '1px solid rgba(232,92,53,0.2)', borderRadius: '10px', padding: '12px 14px', fontSize: '13px', color: 'var(--signal)', marginBottom: '20px', textAlign: 'center' }}>
            🎙 First time setup — creating admin account
          </div>
        )}

        {error && <div style={errorStyle}>{error}</div>}

        {mode === 'setup' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={labelStyle}>Your Name</label>
            <input style={inputStyle} placeholder="Full name" value={form.name}
              onChange={e => setForm({...form, name: e.target.value})}
              onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
          </div>
        )}

        <div style={{ marginBottom: '16px' }}>
          <label style={labelStyle}>Email</label>
          <input style={inputStyle} type="email" placeholder="you@denneryfm.com" value={form.email}
            onChange={e => setForm({...form, email: e.target.value})}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>

        <div style={{ marginBottom: '24px' }}>
          <label style={labelStyle}>Password</label>
          <input style={inputStyle} type="password" placeholder="••••••••" value={form.password}
            onChange={e => setForm({...form, password: e.target.value})}
            onKeyDown={e => e.key === 'Enter' && handleSubmit()} />
        </div>

        <button style={btnStyle} onClick={handleSubmit} disabled={loading}>
          {loading ? 'Please wait…' : mode === 'setup' ? 'Create admin account' : 'Sign in →'}
        </button>

        <div style={{ textAlign: 'center', marginTop: '24px', fontSize: '11px', color: 'var(--text-muted)' }}>
          Staff and DJ access only
        </div>
      </div>
    </div>
  );
}
